import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatDate, getLatest } from "@/utils/anime";

const PAGE_SIZE = 12;

export default function LatestPage() {
  const latest = useMemo(() => getLatest(40), []);
  const [visible, setVisible] = useState(PAGE_SIZE);

  usePageMeta({
    title: "最新更新 - 色情动漫作品更新时间线",
    description: "色情动漫最新更新页按时间倒序整理近期上架与更新剧集的成人向动画作品，含更新日期、连载状态与集数信息。",
    keywords: "色情动漫,最新动漫,动漫更新,成人动漫,新番,动漫时间线",
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const groups = useMemo(() => {
    const map = new Map<string, typeof latest>();
    latest.slice(0, visible).forEach((item) => {
      const key = item.updatedAt.slice(0, 7);
      const list = map.get(key) || [];
      list.push(item);
      map.set(key, list);
    });
    return Array.from(map.entries());
  }, [latest, visible]);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1 w-full">
        <section className="w-full border-b border-background-200/70 bg-background-100/60">
          <div className="w-full px-4 md:px-8 lg:px-12 pt-8 pb-10">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-accent-400">最新更新</span>
            </nav>

            <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] tracking-[0.3em] text-accent-500 mb-2">TIMELINE</p>
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 tracking-wide">最新更新</h1>
                <p className="mt-3 text-sm text-foreground-500 leading-relaxed max-w-2xl">
                  按更新时间倒序排列的全部作品。每一组代表一个更新月份，方便你确认最近上线的剧集与状态变化。
                </p>
              </div>
              <div className="flex items-center gap-6">
                <div>
                  <p className="text-[11px] text-foreground-500">最近更新</p>
                  <p className="mt-1 text-sm text-accent-400">{formatDate(latest[0]?.updatedAt || "")}</p>
                </div>
                <div>
                  <p className="text-[11px] text-foreground-500">收录作品</p>
                  <p className="mt-1 font-label text-2xl text-accent-400 leading-none tracking-widest">{latest.length}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-12">
          <div className="flex flex-col gap-10">
            {groups.map(([month, items]) => (
              <div key={month} className="grid grid-cols-1 lg:grid-cols-[120px_1fr] gap-4 lg:gap-8">
                <div className="lg:sticky lg:top-24 h-fit">
                  <p className="font-label text-3xl text-accent-500 leading-none tracking-wider">{month.replace("-", ".")}</p>
                  <p className="mt-2 text-xs text-foreground-500">{items.length} 部作品更新</p>
                  <span className="hidden lg:block w-px h-16 bg-background-300/60 mt-4" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-7">
                  {items.map((item) => (
                    <AnimeCard key={item.id} anime={item} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col items-center gap-3">
            <p className="text-xs text-foreground-500">
              已显示 {Math.min(visible, latest.length)} / {latest.length} 部
            </p>
            {visible < latest.length ? (
              <button
                type="button"
                onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                className="h-11 px-8 rounded-md border border-accent-600/50 text-accent-400 hover:bg-accent-500/10 text-sm flex items-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
              >
                加载更多更新
                <i className="ri-arrow-down-line" />
              </button>
            ) : (
              <span className="text-xs text-foreground-400">已经到底了</span>
            )}
          </div>

          <div className="mt-12 rounded-lg border border-background-300/50 bg-background-100/60 p-5 md:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-sm text-foreground-900 tracking-wide">想看更早的作品？</h2>
              <p className="mt-1.5 text-xs text-foreground-500">按分类与年份筛选，能找到更早完结的作品资料。</p>
            </div>
            <Link
              to="/category"
              className="h-10 px-5 rounded-md bg-primary-600 hover:bg-primary-500 text-foreground-950 text-sm flex items-center justify-center transition-colors whitespace-nowrap"
            >
              进入分类筛选
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}